<template>
  <section>

    
    <div
      class="px-4 mx-auto max-w-3xl lg:px-0"
      :class="[
        paddingTop === 'reduced' ? 'pt-6' : 'pt-4 lg:pt-4',
        paddingBottom === 'reduced' ? 'pb-6' : 'pb-8 lg:pb-4'
      ]"
    >
      <div class="overflow-hidden rounded-xl border border-[#E0D5C5] bg-white pb-6">
        <!-- Header -->
        <div class="flex flex-col items-start justify-between gap-4 bg-[#3B2314] px-6 py-4 sm:flex-row sm:items-center">
          <div class="flex gap-3.5 items-center min-w-0">
            <nuxt-img
              v-if="headerIconSrc"
              :src="headerIconSrc"
              :alt="headerIconAlt"
              width="44"
              height="44"
              format="webp"
              loading="lazy"
              sizes="44px"
              class="size-11 shrink-0"
            />
            <div
              v-else
              class="size-11 shrink-0 rounded-lg bg-[#2A2A2A]"
              aria-hidden="true"
            />

            <div class="min-w-0">
              <p
                class="mb-1 text-white font-ww-bold text-22 leading-27"
                v-html="productTitle"
              />
              <p
                v-if="productSubtitle"
                class="font-ww-regular text-xs uppercase tracking-wide text-[#C8A882]"
                v-html="productSubtitle"
              />
            </div>
          </div>

          <a
            v-if="shopButtonHref"
            :href="shopButtonHref"
            :aria-label="shopButtonAriaLabel"
            class="inline-flex gap-2.5 items-center px-8 py-3 text-lg text-white rounded-full btn-hover-lift shrink-0 bg-lifeboost-red font-ww-bold hover:opacity-90 md-max:mx-auto"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span v-html="shopButtonText" />
            <svg
              class="size-3.5 shrink-0"
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M0.000273357 7.93934L10.5559 7.93934L6.5581 12.6702L8.04351 13.9294L13.9297 6.96471L8.04351 -2.57293e-07L6.5581 1.25922L10.5559 5.99008L0.000273443 5.99008L0.000273357 7.93934Z"
                fill="white"
              />
            </svg>
          </a>
          <button
            v-else
            type="button"
            :aria-label="shopButtonAriaLabel"
            class="inline-flex gap-2.5 items-center px-6 py-4 text-lg text-white rounded-full btn-hover-lift shrink-0 bg-lifeboost-red font-ww-bold hover:opacity-90"
          >
            <span v-html="shopButtonText" />
            <svg
              class="rotate-90 size-3.5 shrink-0"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M8 3V13M8 13L4 9M8 13L12 9"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>

        <!-- Body -->
        <div class="px-6 pt-6">
          <div
            class="flex flex-col gap-6 w-full lg:flex-row lg:items-start"
            :class="hasSidebar ? '' : 'lg:items-center lg:gap-4'"
          >
            <div
              class="min-w-0 flex-1 font-ww-regular text-17 leading-6 text-[#4A4A4A] [&_a]:font-ww-bold [&_a]:underline [&_p+p]:mt-5 [&_sup]:text-xs"
              v-html="introContent"
            />

            <div
              class="flex flex-col gap-6 items-center mx-auto w-full shrink-0 lg:mx-0"
              :class="productImageContainerClass"
            >
              <nuxt-img
                v-if="productImageSrc"
                :src="productImageSrc"
                :alt="productImageAlt"
                :width="productImageWidth"
                :height="productImageHeight"
                format="webp"
                loading="lazy"
                :sizes="productImageSizes"
                class="object-cover mx-auto w-full"
                :class="productImageMediaClass"
              />
              <div
                v-else
                class="mx-auto w-full bg-[#2A2A2A]"
                :class="productImageMediaClass"
                aria-hidden="true"
              />

              <ul
                v-if="features.length"
                class="flex flex-wrap gap-3.5 justify-center items-start w-full"
              >
                <li
                  v-for="(feature, index) in features"
                  :key="index"
                  class="inline-flex h-12 items-center gap-4 rounded-full bg-[#EEF6FE] px-4 py-2.5"
                >
                  <div
                    class="size-7.5 shrink-0 overflow-hidden rounded-full bg-[#2A2A2A]"
                    aria-hidden="true"
                  >
                    <img
                      v-if="feature.iconSrc && isFeatureIconSvg(feature.iconSrc)"
                      :src="feature.iconSrc"
                      alt=""
                      width="30"
                      height="30"
                      loading="lazy"
                      class="object-cover w-8 size-full"
                    />
                    <nuxt-img
                      v-else-if="feature.iconSrc"
                      :src="feature.iconSrc"
                      alt=""
                      width="30"
                      height="30"
                      format="webp"
                      loading="lazy"
                      sizes="30px"
                      class="object-cover w-8 size-full"
                    />
                  </div>
                  <span
                    class="text-xs text-black whitespace-nowrap font-ww-regular"
                    v-html="feature.label"
                  />
                </li>
              </ul>
            </div>
          </div>

          <ul
            v-if="ingredients.length"
            class="grid grid-cols-1 gap-2.5 mt-6 sm:grid-cols-2"
          >
            <li
              v-for="(ingredient, index) in ingredients"
              :key="index"
              class="rounded-md border border-[#E0D5C5] bg-[#FAF6F0] px-4 py-3"
            >
              <div class="flex gap-3.5 items-start">
                <div class="size-22.5 shrink-0 overflow-hidden rounded-lg border border-[#E0D5C5] bg-[#2A2A2A]">
                  <nuxt-img
                    v-if="ingredient.imageSrc"
                    :src="ingredient.imageSrc"
                    :alt="ingredient.imageAlt || ingredient.title"
                    width="90"
                    height="90"
                    format="webp"
                    loading="lazy"
                    sizes="90px"
                    class="object-cover size-full"
                  />
                </div>

                <div class="min-w-0">
                  <p
                    class="font-ww-bold text-15 leading-26 text-[#6B3A22]"
                    v-html="ingredient.title"
                  />
                  <p
                    class="font-ww-regular text-sm leading-6 text-[#4A4A4A]"
                    v-html="ingredient.description"
                  />
                </div>
              </div>
            </li>
          </ul>

          <ul
            v-if="statCards.length"
            class="grid grid-cols-1 gap-6 mt-6 sm:grid-cols-3"
          >
            <li
              v-for="(card, index) in statCards"
              :key="index"
              class="flex flex-col gap-2.5"
            >
              <nuxt-img
                v-if="card.imageSrc"
                :src="card.imageSrc"
                :alt="card.imageAlt"
                width="220"
                height="173"
                format="webp"
                loading="lazy"
                sizes="xs:100vw sm:220px lg:220px"
                class="w-full rounded-lg object-cover aspect-[220/173]"
              />
              <div
                v-else
                class="w-full rounded-lg bg-[#2A2A2A] aspect-[220/173]"
                aria-hidden="true"
              />
              <p
                class="font-ww-regular text-base leading-27 text-[#4A4A4A] [&_sup]:text-xs"
                v-html="card.text"
              />
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
const props = defineProps({
  headerIconSrc: {
    type: String,
    default: '/assets/images/cognition-showcase-header-icon.svg'
  },
  headerIconAlt: {
    type: String,
    default: 'Cognition Coffee icon'
  },
  productTitle: {
    type: String,
    default: 'Cognition Mushroom Coffee'
  },
  productSubtitle: {
    type: String,
    default: 'The Only Low-Acid Mushroom Coffee'
  },
  shopButtonText: {
    type: String,
    default: 'Shop Now'
  },
  shopButtonHref: {
    type: String,
    default: 'https://lifeboostcoffee.com/products/cognition-mushroom-coffee'
  },
  introContent: {
    type: String,
    default: `<p><a href="https://lifeboostcoffee.com/products/cognition-mushroom-coffee" target="_blank" rel="noopener noreferrer"><strong>Cognition Coffee</strong></a> is our mushroom-infused coffee that doesn't taste like mushroom coffee but still delivers all the benefits of such an infusion.</p>
<p>Here, we begin with our premium, low acid, stomach-friendly, non-GMO, 3rd party tested, sun dried, and spring water washed Central American coffee, these clean, pure beans then infused with lion's mane and chaga mushroom extracts.</p>
<p>As you likely know, these mushrooms are renowned for their potential cognitive-enhancing properties, and here, in Lifeboost Cognition Coffee, we've used extracts 8 times more potent than other mushroom coffees, making this the only low-acid mushroom coffee on the market with unparalleled strength.</p>
<p>When it comes to supporting the health and functionality of your brain, Lifeboost Cognition Coffee delivers!</p>
<p>And, if mushroom coffee isn't your thing, when sipping clean, healthy, sustainably grown, low acid, toxin-tested <a href="https://lifeboostcoffee.com/" target="_blank" rel="noopener noreferrer"><strong>Lifeboost Coffee</strong></a> of <u>any roast/flavor</u>, can still support powerful brain boosting benefits!</p>`
  },
  productImageSrc: {
    type: String,
    default: '/assets/images/cognition-showcase-product-mens.png'
  },
  productImageAlt: {
    type: String,
    default: 'Lifeboost Cognition Mushroom Coffee bag'
  },
  productImageWidth: {
    type: Number,
    default: 243
  },
  productImageHeight: {
    type: Number,
    default: 379
  },
  productImageRounded: {
    type: Boolean,
    default: false
  },
  ingredients: {
    type: Array,
    default: () => []
  },
  features: {
    type: Array,
    default: () => [
      { label: 'Single origin', iconSrc: '/assets/images/cognition-feature-single-origin.svg', iconAlt: 'Single origin' },
      { label: 'Non GMO', iconSrc: '/assets/images/cognition-feature-non-gmo.svg', iconAlt: 'Non GMO' },
      { label: '3rd party tested', iconSrc: '/assets/images/cognition-feature-3rd-party.png', iconAlt: '3rd party tested' },
      { label: 'Hand picked', iconSrc: '/assets/images/cognition-feature-hand-picked.svg', iconAlt: 'Hand picked' },
      { label: 'Sun dried', iconSrc: '/assets/images/cognition-feature-sun-dried.png', iconAlt: 'Sun dried' },
      { label: 'Spring water washed', iconSrc: '/assets/images/cognition-feature-spring-water.png', iconAlt: 'Spring water washed' }
    ]
  },
  statCards: {
    type: Array,
    default: () => [
      {
        text: 'Studies show regular coffee drinkers have a 29% lower risk of Parkinson\'s disease and a 28% decreased risk of dementia.',
        imageSrc: '/assets/images/cognition-stat-parkinson-mens.jpg',
        imageAlt: 'Person in wheelchair'
      },
      {
        text: 'Regular coffee consumption is also linked to improvements in reaction time, attention, learning, and overall cognitive function.',
        imageSrc: '/assets/images/cognition-stat-reaction-mens.jpg',
        imageAlt: 'Man with apple on head'
      },
      {
        text: 'Studies suggest regular coffee consumption has a profoundly positive effect on memory (short and long term).<sup>17</sup>',
        imageSrc: '/assets/images/cognition-stat-memory-mens.jpg',
        imageAlt: 'Digital brain illustration'
      }
    ]
  },
  paddingTop: {
    type: String,
    default: 'reduced',
    validator: (value) => ['default', 'reduced'].includes(value)
  },
  paddingBottom: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'reduced'].includes(value)
  }
})

const isFeatureIconSvg = (src) => src?.toLowerCase().endsWith('.svg')

const stripRichText = (value) => (value ?? '').replace(/<[^>]*>/g, '').trim()

const shopButtonAriaLabel = computed(() => {
  const buttonText = stripRichText(props.shopButtonText) || 'Shop Now'
  const productName = stripRichText(props.productTitle)

  return productName ? `${buttonText} - ${productName}` : buttonText
})

const hasSidebar = computed(() => props.features.length > 0 || props.statCards.length > 0)

const productImageContainerClass = computed(() =>
  hasSidebar.value ? 'max-w-xs' : 'max-w-[280px]'
)

const productImageMediaClass = computed(() => {
  const classes = [
    hasSidebar.value
      ? 'max-w-60 aspect-[243/379]'
      : `aspect-[${props.productImageWidth}/${props.productImageHeight}]`,
    props.productImageRounded ? 'rounded-2xl' : ''
  ]

  return classes.filter(Boolean).join(' ')
})

const productImageSizes = computed(() => {
  if (hasSidebar.value) {
    return 'xs:100vw sm:243px lg:243px'
  }

  return `xs:100vw sm:${props.productImageWidth}px lg:${props.productImageWidth}px`
})

defineBuilderComponent({
  friendlyName: 'Content - Product Showcase Card',
  inputs: [
    {
      name: 'headerIconSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg'],
      defaultValue: '/assets/images/cognition-showcase-header-icon.svg'
    },
    {
      name: 'headerIconAlt',
      type: 'text',
      defaultValue: 'Cognition Coffee icon'
    },
    {
      name: 'productTitle',
      type: 'richText',
      defaultValue: 'Cognition Mushroom Coffee'
    },
    {
      name: 'productSubtitle',
      type: 'text',
      defaultValue: 'The Only Low-Acid Mushroom Coffee'
    },
    {
      name: 'shopButtonText',
      type: 'text',
      defaultValue: 'Shop Now'
    },
    {
      name: 'shopButtonHref',
      type: 'text',
      defaultValue: 'https://lifeboostcoffee.com/products/cognition-mushroom-coffee'
    },
    {
      name: 'introContent',
      type: 'richText',
      defaultValue: `<p><a href="https://lifeboostcoffee.com/products/cognition-mushroom-coffee" target="_blank" rel="noopener noreferrer"><strong>Cognition Coffee</strong></a> is our mushroom-infused coffee that doesn't taste like mushroom coffee but still delivers all the benefits of such an infusion.</p>
<p>Here, we begin with our premium, low acid, stomach-friendly, non-GMO, 3rd party tested, sun dried, and spring water washed Central American coffee, these clean, pure beans then infused with lion's mane and chaga mushroom extracts.</p>
<p>As you likely know, these mushrooms are renowned for their potential cognitive-enhancing properties, and here, in Lifeboost Cognition Coffee, we've used extracts 8 times more potent than other mushroom coffees, making this the only low-acid mushroom coffee on the market with unparalleled strength.</p>
<p>When it comes to supporting the health and functionality of your brain, Lifeboost Cognition Coffee delivers!</p>
<p>And, if mushroom coffee isn't your thing, when sipping clean, healthy, sustainably grown, low acid, toxin-tested <a href="https://lifeboostcoffee.com/" target="_blank" rel="noopener noreferrer"><strong>Lifeboost Coffee</strong></a> of <u>any roast/flavor</u>, can still support powerful brain boosting benefits!</p>`
    },
    {
      name: 'productImageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
      defaultValue: '/assets/images/cognition-showcase-product-mens.png'
    },
    {
      name: 'productImageAlt',
      type: 'text',
      defaultValue: 'Lifeboost Cognition Mushroom Coffee bag'
    },
    {
      name: 'productImageRounded',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'ingredients',
      type: 'list',
      defaultValue: [],
      subFields: [
        {
          name: 'imageSrc',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
          defaultValue: ''
        },
        {
          name: 'imageAlt',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'title',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'description',
          type: 'longText',
          defaultValue: ''
        }
      ]
    },
    {
      name: 'features',
      type: 'list',
      defaultValue: [
        { label: 'Single origin', iconSrc: '/assets/images/cognition-feature-single-origin.svg', iconAlt: 'Single origin' },
        { label: 'Non GMO', iconSrc: '/assets/images/cognition-feature-non-gmo.svg', iconAlt: 'Non GMO' },
        { label: '3rd party tested', iconSrc: '/assets/images/cognition-feature-3rd-party.png', iconAlt: '3rd party tested' },
        { label: 'Hand picked', iconSrc: '/assets/images/cognition-feature-hand-picked.svg', iconAlt: 'Hand picked' },
        { label: 'Sun dried', iconSrc: '/assets/images/cognition-feature-sun-dried.png', iconAlt: 'Sun dried' },
        { label: 'Spring water washed', iconSrc: '/assets/images/cognition-feature-spring-water.png', iconAlt: 'Spring water washed' }
      ],
      subFields: [
        {
          name: 'iconSrc',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg'],
          defaultValue: ''
        },
        {
          name: 'iconAlt',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'label',
          type: 'text',
          defaultValue: ''
        }
      ]
    },
    {
      name: 'statCards',
      type: 'list',
      defaultValue: [
        {
          text: 'Studies show regular coffee drinkers have a 29% lower risk of Parkinson\'s disease and a 28% decreased risk of dementia.',
          imageSrc: '/assets/images/cognition-stat-parkinson-mens.jpg',
          imageAlt: 'Person in wheelchair'
        },
        {
          text: 'Regular coffee consumption is also linked to improvements in reaction time, attention, learning, and overall cognitive function.',
          imageSrc: '/assets/images/cognition-stat-reaction-mens.jpg',
          imageAlt: 'Man with apple on head'
        },
        {
          text: 'Studies suggest regular coffee consumption has a profoundly positive effect on memory (short and long term).<sup>17</sup>',
          imageSrc: '/assets/images/cognition-stat-memory-mens.jpg',
          imageAlt: 'Digital brain illustration'
        }
      ],
      subFields: [
        {
          name: 'imageSrc',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
          defaultValue: ''
        },
        {
          name: 'imageAlt',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'text',
          type: 'richText',
          defaultValue: ''
        }
      ]
    },
    {
      name: 'paddingTop',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'reduced'
    },
    {
      name: 'paddingBottom',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'default'
    }
  ]
})
</script>
